import { useEffect, useState, useMemo } from "react";
import React from 'react';
import { useTranslation } from "react-i18next";
import { Typography, Box, IconButton, Snackbar, Grid, Button, Modal, ModalDialog, ModalClose, DialogTitle, Divider, DialogContent, DialogActions, CssBaseline, Tooltip, Breadcrumbs, Accordion, AccordionSummary, AccordionDetails, Input, Alert, Sheet, Stack, Chip, FormControl, FormLabel, Textarea, FormHelperText } from "@mui/joy";
import { Edit, KeyboardArrowRight, Add, ExpandMore, Receipt, Close } from "@mui/icons-material";
import Link from '@mui/joy/Link';
import DeleteAlert from "./../../main/widgets/DeleteAlert";
import CustomerOptionList from "../../main/widgets/CustomersOptionList";
import CustomTextField from "../../main/widgets/CustomTextField";
import { deleteShipments, getShipments, getDeletedShipments, recoverShipments, bulkSendShipmentsWhatsappMessage, recalculateShipmentsPrice, bulkUpdateShipments, bulkShipmentTags, receiveShipmentsRequest } from "../../main/API/shipment";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { red, orange, blue, grey } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShipmentsStickersWindow from "./widgets/ShipmentsStickersWindow";
import ShipmentTrackingModal from "./widgets/ShipmentTrackingModal";
import ShipmentPiecesStickersWindow from "../../shipment_pieces/views/widgets/ShipmentPiecesStickersWindow";
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ShipmentTypeOptionList from "../../main/widgets/ShipmentTypeOptionList";
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { DirectionalMaterialThemeProvider } from "../../main/theme/DirectionalMaterialThemeProvider";
import StatusOptionList from "../../main/widgets/StatusOptionList";
import LocationOptionList from "../../main/widgets/LocationOptionList";
import TrackingEventTypeOptionList from "../../main/widgets/TrackingEventTypeOptionList";
import ListAltIcon from '@mui/icons-material/ListAlt';
import LabelIcon from '@mui/icons-material/Label';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LaunchIcon from '@mui/icons-material/Launch';
import TimelineIcon from '@mui/icons-material/Timeline';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import PaymentMethodOptionList from "../../main/widgets/PaymentMethodOptionList";
import AccountOptionList from "../../main/widgets/AccountOptionList";
import PaymentStatusOptionList, { getPaymentStatusIconConfig, getPaymentStatusChipStyle, formatPaymentStatus } from "../../main/widgets/PaymentStatusOptionList";
import { MuiPhone } from "../../main/widgets/MuiPhone";
import { addShipmentPayment } from "../../main/API/payment";
import { isPositivePaymentAmount } from "../../main/constants/paymentMethods";
import InvoicedOptionList from "../../main/widgets/InvoicedOptionList";
import { generateInvoicesForShipmentsRequest } from "../../main/API/invoices";
import { usePermissions } from "../../main/hooks/usePermissions";
import TrackingEventControlFields from "../../main/widgets/TrackingEventControlFields";
import {
  applyTrackingEventControl,
  defaultDatetimeLocalValue,
  trackingDecisionFromFormFields,
  validateTrackingEventFormFields,
} from "../../main/utils/trackingEventControl";
import PermissionGate from "../../main/components/PermissionGate";
import { PERMISSIONS } from "../../main/context/AuthContext";
import { ShipmentTagChips } from "../../main/widgets/shipmentTagUi";
import { OmrAmount } from "../../main/components/OmrAmount";
import OmrSymbol from "../../main/components/OmrSymbol";
import NativeCheckbox from "../../main/components/NativeCheckbox";
import { dataGridNativeCheckboxSlots } from "../../main/components/dataGridNativeCheckboxSlots";
import ShipmentTagsOptionList from "../../main/widgets/ShipmentTagsOptionList";
import { fetchTagsQueryParamFromNames } from "../../main/API/shipmentTags";
import ShipmentUnitOptionList from "../../main/widgets/ShipmentUnitOptionList";
import { bulkAssignShipmentsToShipmentUnit } from "../../main/API/shipmentUnit";
import { getLocationDisplayName } from "../../main/utils/locationDisplayName";
import { getLocalizedUserFullName } from "../../main/utils/userDisplayName";
import {
  getInvoiceGenerationNotification,
  isZeroOrMissingShipmentPrice,
  parseGenerateInvoicesForShipmentsError,
} from "../../main/utils/invoiceGenerationResponse";
import {
  isRowSelectedInSelectionModel,
  selectedShipmentsHaveDifferentInvoices,
} from "../../main/utils/shipmentInvoiceId";
import CustomerDeliveryDestinationForm from "../../home/views/widgets/CustomerDeliveryDestinationForm";
import { useSiteSettings } from "../../main/context/SiteSettingsContext";
import { getSiteDisplayName } from "../../main/utils/siteDisplayName";

/** Parse sent count from bulk WhatsApp API body (or English "N message(s) sent"). */
function getBulkWhatsappSentCount(data, fallback) {
  if (data && typeof data === "object") {
    const candidates = [data.count, data.sent_count, data.messages_sent, data.message_count];
    for (const value of candidates) {
      const n = Number(value);
      if (value != null && !Number.isNaN(n)) return n;
    }
    const raw = typeof data.message === "string" ? data.message.trim() : "";
    const match = raw.match(/(\d+)\s+messages?\s+sent/i);
    if (match) return Number(match[1]);
  }
  return Number(fallback) || 0;
}

/** Localized shipment status label for chips / summary. */
function formatShipmentStatusLabel(status, t) {
  if (!status) return '—';
  const key = String(status).toLowerCase().trim().replace(/\s+/g, '_');
  const withSpaces = key.replace(/_/g, ' ');
  const map = {
    shipping_soon: 'widgets.shipmentStatusShippingSoon',
    'shipping soon': 'widgets.shipmentStatusShippingSoon',
    shipped: 'widgets.shipmentStatusShipped',
    partially_shipped: 'shipmentPieces.status_partiallyShipped',
    arrived: 'widgets.shipmentStatusArrived',
    out_for_delivery: 'widgets.shipmentStatusOutForDelivery',
    'out for delivery': 'widgets.shipmentStatusOutForDelivery',
    partially_delivered: 'shipmentPieces.status_partiallyDelivered',
    delivered: 'widgets.shipmentStatusDelivered',
  };
  const labelKey = map[key] || map[withSpaces];
  return labelKey ? t(labelKey) : status;
}

/** Soft chip colors for shipment status breakdown. */
function shipmentStatusChipStyle(status) {
  const styles = {
    shipping_soon: { bgcolor: '#f1f5f9', color: '#64748b' },
    'shipping soon': { bgcolor: '#f1f5f9', color: '#64748b' },
    shipped: { bgcolor: '#eef2ff', color: '#4f46e5' },
    partially_shipped: { bgcolor: '#e0e7ff', color: '#4338ca' },
    arrived: { bgcolor: '#eff6ff', color: '#2563eb' },
    out_for_delivery: { bgcolor: '#fffbeb', color: '#d97706' },
    'out for delivery': { bgcolor: '#fffbeb', color: '#d97706' },
    partially_delivered: { bgcolor: '#fef3c7', color: '#b45309' },
    delivered: { bgcolor: '#ecfdf5', color: '#059669' },
  };
  const key = status && String(status).toLowerCase().trim().replace(/\s+/g, '_');
  const withSpaces = key && key.replace(/_/g, ' ');
  return styles[key] || styles[withSpaces] || { bgcolor: '#f1f5f9', color: '#64748b' };
}

/** Count occurrences of a key on selected rows (empty → "—"). */
function countByKey(rows, getKey) {
  const counts = {};
  for (const row of rows) {
    const raw = getKey(row);
    const key = raw == null || String(raw).trim() === '' ? '—' : String(raw);
    counts[key] = (counts[key] || 0) + 1;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1]);
}

/** Aggregate KPIs for the current row selection. */
function buildSelectedShipmentsSummary(shipments, language) {
  const rows = Array.isArray(shipments) ? shipments : [];
  let totalPrice = 0;
  let totalPackages = 0;
  let totalWeight = 0;
  let totalVolWeight = 0;
  let piecesCount = 0;
  let invoicedCount = 0;
  let notInvoicedCount = 0;
  let zeroPriceCount = 0;
  let assignedToUnitCount = 0;
  const customerKeys = new Set();
  const locationKeys = new Set();
  const tagCounts = {};

  for (const row of rows) {
    const price = Number(row.price);
    if (!Number.isNaN(price)) totalPrice += price;
    if (isZeroOrMissingShipmentPrice(row.price)) zeroPriceCount += 1;

    const pkgs = Number(row.packages_quantity);
    if (!Number.isNaN(pkgs)) totalPackages += pkgs;

    const weight = Number(row.weight);
    if (!Number.isNaN(weight)) totalWeight += weight;

    const vol = Number(row.volumetric_weight);
    if (!Number.isNaN(vol)) totalVolWeight += vol;

    if (Array.isArray(row.pieces)) piecesCount += row.pieces.length;

    if (row.invoiced) invoicedCount += 1;
    else notInvoicedCount += 1;

    const unitSerial = row.shipment_unit_serial_number;
    if (unitSerial != null && String(unitSerial).trim() !== '') assignedToUnitCount += 1;

    const owner = row.owner;
    if (owner && typeof owner === 'object') {
      const id = owner.id != null ? `id:${owner.id}` : getLocalizedUserFullName(owner, language) || null;
      if (id) customerKeys.add(String(id));
    } else if (owner != null && owner !== '') {
      customerKeys.add(String(owner));
    }

    if (row.location) {
      const locName = getLocationDisplayName(row.location, language) || String(row.location.id ?? row.location);
      locationKeys.add(locName);
    }

    const tags = Array.isArray(row.tags) ? row.tags : [];
    for (const tag of tags) {
      const name = typeof tag === 'string' ? tag : (tag?.name || tag?.label || '');
      if (!name) continue;
      tagCounts[name] = (tagCounts[name] || 0) + 1;
    }
  }

  return {
    count: rows.length,
    totalPrice,
    totalPackages,
    totalWeight,
    totalVolWeight,
    piecesCount,
    invoicedCount,
    notInvoicedCount,
    zeroPriceCount,
    assignedToUnitCount,
    notAssignedToUnitCount: rows.length - assignedToUnitCount,
    uniqueCustomers: customerKeys.size,
    uniqueLocations: locationKeys.size,
    paymentStatusCounts: countByKey(rows, (r) => r.payment_status || 'unpaid'),
    statusCounts: countByKey(rows, (r) => r.status),
    locationCounts: countByKey(rows, (r) => (
      r.location ? (getLocationDisplayName(r.location, language) || String(r.location.id ?? r.location)) : null
    )),
    tagCounts: Object.entries(tagCounts).sort((a, b) => b[1] - a[1]).slice(0, 8),
  };
}

export default function ShipmentManagerScreen() {
  const { t, i18n } = useTranslation("ui");
  const { settings } = useSiteSettings();
  const siteName = getSiteDisplayName(settings, i18n.language);
  const { hasPermission } = usePermissions();
  const canListLocations = hasPermission(PERMISSIONS.LOCATIONS_LIST);
  const canListCustomers = hasPermission(PERMISSIONS.CUSTOMERS_LIST);
  const canListShipmentTypes = hasPermission(PERMISSIONS.SHIPMENT_TYPES_LIST);
  const canListShipmentUnits = hasPermission(PERMISSIONS.SHIPMENT_UNITS_LIST);
  const canListAccounts = hasPermission(PERMISSIONS.ACCOUNTS_LIST);
  const canListShipments = hasPermission(PERMISSIONS.SHIPMENTS_LIST);
  const canListDeletedShipments = hasPermission(PERMISSIONS.SHIPMENTS_DELETED_LIST);
  const canPrintPieceLabel = hasPermission(PERMISSIONS.SHIPMENT_PIECES_LABEL);
  const canPrintPieceLabelBatch = hasPermission(PERMISSIONS.SHIPMENT_PIECES_LABEL_BATCH);

  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteAlertSingal, setDeleteAlertSingal] = useState(false);
  const [openSendMessageAlert, setOpenSendMessageAlert] = useState(false);
  const [openGenerateInvoicesAlert, setOpenGenerateInvoicesAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkSendShipmentsWhatsappMessageLoading, setBulkSendShipmentsWhatsappMessageLoading] = useState(false);
  const [openRecalculatePriceAlert, setOpenRecalculatePriceAlert] = useState(false);
  const [recalculatePriceLoading, setRecalculatePriceLoading] = useState(false);
  const [successDelete, setSuccessDelete] = useState(false);
  const [failedDelete, setFailedDelete] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState("");
  const [shipmentOwner, setShipmentOwner] = useState(null);
  const [id, setId] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState(null);
  const [shipperReference, setShipperReference] = useState(null);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingInvoicesLoading, setIsGeneratingInvoicesLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [invoiced, setInvoiced] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [type, setType] = useState("");
  const [tagFilterNames, setTagFilterNames] = useState([]);
  // Selected shipment unit from the filter picker (null means no unit filter).
  const [shipmentUnitFilter, setShipmentUnitFilter] = useState(null);

  //General Notification
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [warningNotification, setWarningNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  //shipments stickers controllers 
  const [shipmentsStickersWindowOpened, SetShipmentsStickersWindowOpened] = useState(false);
  const [piecesStickersWindowOpened, setPiecesStickersWindowOpened] = useState(false);
  const [stickerPieces, setStickerPieces] = useState([]);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingModalShipmentId, setTrackingModalShipmentId] = useState(null);


  //Bulk update variables
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  const [openBulkUpdateDialog, setOpenBulkUpdateDialog] = useState(false);
  const [, setPaidBulkUpdate] = useState(null);
  const [currentLocationBulkUpdate, setCurrentLocationBulkUpdate] = useState(null);
  const [statusBulkUpdate, setStatusBulkUpdate] = useState(null);
  const [eventTypeBulkUpdate, setEventTypeBulkUpdate] = useState(null);
  const [bulkUpdateDescription, setBulkUpdateDescription] = useState('');
  const [bulkTrackingEventMode, setBulkTrackingEventMode] = useState('record');
  const [bulkTrackingEventTimestamp, setBulkTrackingEventTimestamp] = useState(defaultDatetimeLocalValue);
  const [bulkTrackingEventTimestampError, setBulkTrackingEventTimestampError] = useState('');
  const [sendMessage, setSendMessage] = useState(false)

  /** Bulk tags: POST /api/shipment/bulk-tags/ */
  const [openBulkTagsDialog, setOpenBulkTagsDialog] = useState(false);
  const [bulkTagsLoading, setBulkTagsLoading] = useState(false);
  const [bulkTagsAction, setBulkTagsAction] = useState('add');
  const [bulkTagNames, setBulkTagNames] = useState([]);

  const [openAssignShipmentsToUnitDialog, setOpenAssignShipmentsToUnitDialog] = useState(false);
  const [assignToUnitShipmentUnit, setAssignToUnitShipmentUnit] = useState(null);
  const [assignShipmentsToUnitLoading, setAssignShipmentsToUnitLoading] = useState(false);

  //Receive Shipments variables
  const [civilId, setCivilId] = useState(null);
  const [receiverName, setReceiverName] = useState(null);
  const [receiverPhoneNumber, setReceiverPhoneNumber] = useState(null);
  const [sendShipmentsMessages, setSendShipmentsMessages] = useState(true);
  const [sendInvoicesMessages, setSendInvoicesMessages] = useState(true);
  const [receiveShipmentStatus, setReceiveShipmentStatus] = useState('delivered');
  const [receiveShipmentEventType, setReceiveShipmentEventType] = useState(null);
  const [receiveDestinationType, setReceiveDestinationType] = useState('home');
  const [receiveDestinationLocation, setReceiveDestinationLocation] = useState('');
  const [isReceiveShipmentLoading, setIsReceiveShipmentLoading] = useState(false);
  const [openReceiveShipmentsAlert, setOpenReceiveShipmentsAlert] = useState(false);


  //Pay Shipments variables
  const [isPayShipmentLoading, setIsPayShipmentLoading] = useState(false);
  const [openPayShipmentsAlert, setOpenPayShipmentsAlert] = useState(false);
  const [totalAmount, setTotalAmount] = useState("");
  const [transactionReference, setTransactionReference] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [account, setAccount] = useState(null);
  // Get today's date in YYYY-MM-DD format for date input
  const getTodayDate = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  /** Payment modal: bank/payment transaction date (not shipment.transaction_date). */
  const [paymentTransactionDate, setPaymentTransactionDate] = useState(getTodayDate);
  /** List filters: shipment business date range (API: transaction_date__gte / __lte). */
  const [shipmentTransactionDateGte, setShipmentTransactionDateGte] = useState("");
  const [shipmentTransactionDateLte, setShipmentTransactionDateLte] = useState("");
  const [zeroPriceHighlightIds, setZeroPriceHighlightIds] = useState([]);

  const selectedShipments = useMemo(
    () => results.filter((row) => isRowSelectedInSelectionModel(selectionModel, row.id)),
    [results, selectionModel]
  );
  const hasUninvoicedSelected = useMemo(
    () => selectedShipments.some((row) => !row.invoiced),
    [selectedShipments]
  );
  const hasZeroPriceSelected = useMemo(
    () => selectedShipments.some((row) => isZeroOrMissingShipmentPrice(row.price)),
    [selectedShipments]
  );
  const isGenerateInvoicesDisabled =
    selectionModel.length <= 0
    || selectedShipments.some((row) => row.invoiced || row.paid)
    || hasZeroPriceSelected;
  const generateInvoicesTooltip = hasZeroPriceSelected
    ? t('shipments.tooltipGenerateInvoicesZeroPrice')
    : t('shipments.tooltipGenerateInvoices');
  const zeroPriceHighlightSet = useMemo(
    () => new Set(zeroPriceHighlightIds.map((id) => Number(id))),
    [zeroPriceHighlightIds]
  );
  const hasDifferentInvoicesSelected = useMemo(
    () => selectionModel.length > 0
      && selectedShipmentsHaveDifferentInvoices(selectedShipments),
    [selectionModel.length, selectedShipments]
  );
  const isPayShipmentsDisabled =
    selectionModel.length <= 0
    || hasUninvoicedSelected
    || hasDifferentInvoicesSelected;
  const payShipmentsTooltip = selectionModel.length <= 0
    ? t('shipments.tooltipPaySelect')
    : hasUninvoicedSelected
      ? t('shipments.tooltipPayInvoiceFirst')
      : hasDifferentInvoicesSelected
        ? t('shipments.paymentDifferentInvoicesNote')
        : t('shipments.tooltipMarkPaid');

  useEffect(() => {
    setZeroPriceHighlightIds([]);
  }, [selectionModel]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  // Deleted shipments view: 'active' | 'deleted'
  const [viewMode, setViewMode] = useState('active');
  const [deletedData, setDeletedData] = useState(null);
  const [deletedResults, setDeletedResults] = useState([]);
  const [deletedPaginationModel, setDeletedPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedSelectionModel, setDeletedSelectionModel] = useState([]);
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [openRecoverAlert, setOpenRecoverAlert] = useState(false);

  const selectedDeletedShipments = useMemo(
    () => deletedResults.filter((row) => isRowSelectedInSelectionModel(deletedSelectionModel, row.id)),
    [deletedResults, deletedSelectionModel]
  );
  const selectedSummary = useMemo(
    () => buildSelectedShipmentsSummary(
      viewMode === 'deleted' ? selectedDeletedShipments : selectedShipments,
      i18n.language,
    ),
    [viewMode, selectedDeletedShipments, selectedShipments, i18n.language]
  );

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  //table payment status icon (colors from PaymentStatusOptionList)
  function paymentStatusIcon(paymentStatus) {
    const config = getPaymentStatusIconConfig(paymentStatus);
    return (
      <Tooltip title={config.title}>
        <MonetizationOnIcon
          color={config.color === 'success' ? 'success' : undefined}
          sx={config.color !== 'success' ? { color: config.color } : undefined}
        />
      </Tooltip>
    );
  }

  //table invoiced icon
  function invoicedIcon(invoiced) {
    if (invoiced) {
      return (
        <Tooltip title={t("shipments.tooltipInvoiced")}>
          <Receipt color='success'></Receipt>
        </Tooltip>
      );
    }
    else {
      return (
        <Tooltip title={t("shipments.tooltipNotInvoiced")}>
          <Receipt sx={{ color: red[500] }}></Receipt>
        </Tooltip>
      );
    }
  }

  //table formate time
  function formateTime(date) {
    var d = new Date(date);
    var time = d.toLocaleTimeString('en-US', { timeZone: 'Asia/Muscat', hour12: true, hour: 'numeric', minute: 'numeric' });

    var dateString = d.getDate() + "/" + (d.getMonth() + 1) + "/" + d.getFullYear() + " " + time;

    return dateString;

  }

  function formatShipmentTransactionDateCell(value) {
    if (value == null || value === "") return "—";
    const s = String(value);
    const ymd = s.length >= 10 ? s.slice(0, 10) : s;
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [y, m, d] = ymd.split("-");
      return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
    }
    return ymd;
  }

  const navigate = useNavigate();


  const columns = [
    { field: 'id', headerName: t("shipments.colId"), flex: 1.5, minWidth: 80 },
    {
      field: 'owner',
      headerName: t("shipments.colCustomer"),
      flex: 3,
      minWidth: 180,
      renderCell: (params) => {
        const owner = params.row.owner;
        if (owner && typeof owner === 'object') {
          const name = getLocalizedUserFullName(owner, i18n.language);
          return name || '—';
        }
        return '';
      }
    },
    { field: 'shipper_reference', headerName: t("shipments.colShipperRef"), flex: 2, minWidth: 150 },
    { field: 'tracking_number', headerName: t("shipments.colTracking"), flex: 2, minWidth: 150 },
    {
      field: 'shipment_unit_serial_number',
      headerName: t("shipments.colUnitSerial"),
      flex: 2,
      minWidth: 150,
      renderCell: (params) => (params.row.shipment_unit_serial_number != null && params.row.shipment_unit_serial_number !== '' ? params.row.shipment_unit_serial_number : '—'),
    },
    { field: 'volumetric_weight', headerName: t("shipments.colVolWeight"), flex: 2, minWidth: 140, type: 'number' },
    { field: 'packages_quantity', headerName: t("shipments.colPackagesQty"), flex: 2, minWidth: 140, type: 'number' },
    // { field: 'charge', headerName: 'Charge', flex: 2, minWidth: 100, type: 'number' },
    {
      field: 'price',
      headerName: t("shipments.colPrice"),
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <span>{t("shipments.colPrice")}</span>
          <OmrSymbol height={14} />
        </Box>
      ),
      flex: 2,
      minWidth: 120,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => <OmrAmount value={params.row.price} empty="—" />,
    },
    { field: 'payment_status', headerName: t("shipments.colPaymentStatus"), flex: 1.5, minWidth: 120, renderCell: (params) => paymentStatusIcon(params.row.payment_status) },
    { field: 'invoiced', headerName: t("shipments.colInvoiced"), flex: 1, minWidth: 80, renderCell: (params) => invoicedIcon(params.row.invoiced) },

    { field: 'status', headerName: t("shipments.colStatus"), flex: 2, minWidth: 120 },
    {
      field: 'tags',
      headerName: t("shipments.colTags"),
      flex: 2.8,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ width: '100%', minWidth: 0, alignSelf: 'flex-start', py: 0.25 }}>
          <ShipmentTagChips tags={Array.isArray(params.row.tags) ? params.row.tags : []} max={6} />
        </Box>
      ),
    },
    {
      field: 'notification', headerName: t("shipments.colNotifications"), flex: 5, minWidth: 200, renderCell: (params) => {
        const notifs = params.row.notifications;
        if (!Array.isArray(notifs) || notifs.length === 0) return '—';
        // Group and count by shipment_status
        const groupedNotifications = notifs.reduce((acc, notification) => {
          const status = notification.shipment_status;
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});

        // Format the result
        const formattedResult = Object.entries(groupedNotifications)
          .map(([status, count]) => `${status.toUpperCase()} (${count})`)
          .join(", ");

        return formattedResult
      }
    },
    { field: 'location', headerName: t("shipments.colLocation"), flex: 2, minWidth: 140, renderCell: (params) => (params.row.location ? getLocationDisplayName(params.row.location, i18n.language) : "") },
    {
      field: 'transaction_date',
      headerName: t("shipments.colTransactionDate"),
      flex: 2,
      minWidth: 130,
      renderCell: (params) => formatShipmentTransactionDateCell(params.row.transaction_date),
    },
    { field: 'created_at', headerName: t("shipments.colCreatedAt"), flex: 2, minWidth: 160, renderCell: (params) => formateTime(params.row.created_at) },
    {
      field: 'actions',
      headerName: t("shipments.colActions"),
      flex: 3,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {hasPermission(PERMISSIONS.TRACKING_EVENTS_LIST) && (
            <Tooltip title={t("shipments.tooltipTrackShipment")}>
              <IconButton
                size="sm"
                variant="soft"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  setTrackingModalShipmentId(params.row.id);
                  setTrackingModalOpen(true);
                }}
              >
                <TimelineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission(PERMISSIONS.SHIPMENTS_UPDATE) && (
            <Tooltip title={t("shipments.tooltipEditShipment")}>
              <IconButton
                size="sm"
                variant="soft"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`Shipment/${params.row.id}/EditShipment`, '_blank');
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission(PERMISSIONS.SHIPMENTS_DELETE) && (
            <Tooltip title={t("shipments.tooltipDeleteShipment")}>
              <IconButton
                size="sm"
                variant="soft"
                color="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedShipment([params.row.id]);
                  setDeleteAlertSingal(true);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Deleted view: same columns but actions = View + Recover
  const columnsDeleted = [
    ...columns.slice(0, -1),
    {
      field: 'actions',
      headerName: t("shipments.colActions"),
      flex: 3,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {hasPermission(PERMISSIONS.TRACKING_EVENTS_LIST) && (
            <Tooltip title={t("shipments.tooltipTrackShipment")}>
              <IconButton
                size="sm"
                variant="soft"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  setTrackingModalShipmentId(params.row.id);
                  setTrackingModalOpen(true);
                }}
              >
                <TimelineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission(PERMISSIONS.SHIPMENTS_VIEW) && (
            <Tooltip title={t("shipments.tooltipViewShipment")}>
              <IconButton
                size="sm"
                variant="soft"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`Shipment/${params.row.id}/EditShipment`, '_blank');
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission(PERMISSIONS.SHIPMENTS_RECOVER) && (
            <Tooltip title={t("shipments.tooltipRecoverShipment")}>
              <IconButton
                size="sm"
                variant="soft"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRecoverOneShipment(params.row.id);
                }}
              >
                <RestoreFromTrashIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  //delete shipments from table 
  const deleteShipmentsFromTable = async (ids) => {
    if (!hasPermission(PERMISSIONS.SHIPMENTS_BULK_DELETE)) {
      setDeleteAlert(false);
      setDeleteAlertSingal(false);
      return;
    }
    setDeleteLoading(true);
    try {
      const response = await deleteShipments(ids);
      if (response.status === 200) {
        await fetchData();
        setSuccessDelete(true);
      } else {
        setFailedDelete(true);
        setDeleteErrorMsg(t("shipments.deleteFailed", { detail: response.data?.detail ?? response.data?.error ?? "" }));
      }
    } catch (e) {
      setFailedDelete(true);
      const res = e.response;
      if (res?.status === 400 && res?.data?.error) {
        const msg = res.data.shipment_ids?.length
          ? t("shipments.deleteErrorWithIds", { error: res.data.error, ids: res.data.shipment_ids.join(", ") })
          : res.data.error;
        setDeleteErrorMsg(msg);
      } else {
        setDeleteErrorMsg(t("shipments.deleteErrorGeneric", { message: e.message }));
      }
    }
    setDeleteLoading(false);
    setDeleteAlert(false);
    setDeleteAlertSingal(false);

  };


  const fetchData = async (isSearch = false) => {
    if (!canListShipments) {
      setData(null);
      setResults([]);
      setIsLoading(false);
      return;
    }
    if (isSearch) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
    setIsLoading(true);
    const tagsQuery =
      Array.isArray(tagFilterNames) && tagFilterNames.length > 0
        ? await fetchTagsQueryParamFromNames(tagFilterNames)
        : null;
    await getShipments(
      shipmentOwner, id, trackingNumber, shipperReference, status, currentLocation, null, invoiced, type, paymentStatus,
      tagsQuery,
      paginationModel.pageSize, isSearch ? 1 : paginationModel.page + 1,
      shipmentTransactionDateGte,
      shipmentTransactionDateLte,
      shipmentUnitFilter
    )
      .then((response) => response?.data)
      .then((actualData) => {
        setData(actualData ?? null);
        setResults(Array.isArray(actualData?.results) ? actualData.results : []);
      })
      .catch((err) => console.log(err.message))
      .finally(() => setIsLoading(false));
  };

  const fetchDeletedData = async (isSearch = false) => {
    if (!canListDeletedShipments) {
      setDeletedData(null);
      setDeletedResults([]);
      setDeletedLoading(false);
      return;
    }
    if (isSearch) {
      setDeletedPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
    setDeletedLoading(true);
    const tagsQuery =
      Array.isArray(tagFilterNames) && tagFilterNames.length > 0
        ? await fetchTagsQueryParamFromNames(tagFilterNames)
        : null;
    await getDeletedShipments(
      shipmentOwner, id, trackingNumber, shipperReference, status, currentLocation, invoiced, type, paymentStatus,
      tagsQuery,
      deletedPaginationModel.pageSize, isSearch ? 1 : deletedPaginationModel.page + 1,
      shipmentTransactionDateGte,
      shipmentTransactionDateLte,
      shipmentUnitFilter
    )
      .then((response) => response?.data)
      .then((actualData) => {
        setDeletedData(actualData);
        setDeletedResults(actualData?.results ?? []);
      })
      .catch((err) => console.log(err?.message))
      .finally(() => setDeletedLoading(false));
  };

  const handleRecoverShipments = async () => {
    if (deletedSelectionModel.length === 0) return;
    if (!hasPermission(PERMISSIONS.SHIPMENTS_RECOVER)) return;
    setRecoverLoading(true);
    setOpenRecoverAlert(false);
    try {
      const response = await recoverShipments(deletedSelectionModel);
      const res = response && response.data;
      const recovered = (res && res.recovered_count) != null ? res.recovered_count : 0;
      const skipped = (res && res.skipped_ids) || [];
      setDeletedSelectionModel([]);
      await fetchDeletedData(true);
      if (recovered > 0) {
        const msg = skipped.length > 0
          ? t("shipments.recoveredWithSkipped", { recovered, skipped: skipped.join(', ') })
          : t("shipments.recoveredToActive", { count: recovered });
        setResponseMessage(msg);
        setSuccessNotification(true);
      }
      if (recovered === 0 && skipped.length > 0) {
        setResponseMessage(t("shipments.noneRecoveredSkipped", { skipped: skipped.join(', ') }));
        setFailedNotification(true);
      }
    } catch (err) {
      const res = err && err.response;
      const msg = (res && res.status === 403)
        ? t("shipments.permissionRecover")
        : (res && res.data && res.data.error) || (err && err.message) || t("shipments.failedRecover");
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setRecoverLoading(false);
    }
  };

  const handleRecoverOneShipment = async (shipmentId) => {
    if (!hasPermission(PERMISSIONS.SHIPMENTS_RECOVER)) return;
    setRecoverLoading(true);
    try {
      const response = await recoverShipments([shipmentId]);
      const res = response && response.data;
      const recovered = (res && res.recovered_count) != null ? res.recovered_count : 0;
      const skipped = (res && res.skipped_ids) || [];
      await fetchDeletedData(true);
      if (recovered > 0) {
        setResponseMessage(t("shipments.shipmentRecoveredSingle"));
        setSuccessNotification(true);
      } else if (skipped.length > 0) {
        setResponseMessage(t("shipments.couldNotRecoverSingle", { skipped: skipped.join(', ') }));
        setFailedNotification(true);
      }
    } catch (err) {
      const res = err && err.response;
      const msg = (res && res.status === 403)
        ? t("shipments.permissionRecover")
        : (res && res.data && res.data.error) || (err && err.message) || t("shipments.failedRecoverSingle");
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setRecoverLoading(false);
    }
  };

  // bulk send whatsapp message for selected shipments
  const bulkSendShipmentsWhatsappMessageControl = async () => {
    if (!hasPermission(PERMISSIONS.WHATSAPP_BULK_SEND_SHIPMENTS)) return;

    setBulkSendShipmentsWhatsappMessageLoading(true);
    await bulkSendShipmentsWhatsappMessage(selectionModel)
      .then(async actualData => {
        // control success
        if (actualData.status == 200) {
          // localize success toast (API message is English-only, e.g. "1 message sent")
          setResponseMessage(t("shipments.whatsappMessagesSent", {
            count: getBulkWhatsappSentCount(actualData.data, selectionModel.length),
          }));
          // send notification
          setSuccessNotification(true);
          setOpenSendMessageAlert(false);
          await fetchData(false);

        }
        // control other responses
        else {
          setResponseMessage(t("shipments.errorSendMessages"));
          // send notification
          setFailedNotification(true);
        }

        // stop loading
        setBulkSendShipmentsWhatsappMessageLoading(false);
      }).catch((err) => {

        // stop loading
        setBulkSendShipmentsWhatsappMessageLoading(false);

        console.log(err.message);
      });

  }

  //Recalculate price for selected shipments 
  const recalculatePriceControl = async () => {
    if (!hasPermission(PERMISSIONS.SHIPMENTS_RECALCULATE_PRICE)) return;

    setRecalculatePriceLoading(true);
    await recalculateShipmentsPrice(selectionModel)
      .then(async actualData => {
        //control success
        if (actualData.status == 200) {
          setResponseMessage(actualData.data.message);
          fetchData(true);
          //send notification
          setSuccessNotification(true);
          setOpenRecalculatePriceAlert(false);

        }
        //control other responses
        else {
          setResponseMessage(t("shipments.errorRecalculatePrice"));
          //send notification
          setFailedNotification(true);
        }

        //stop loading 
        setRecalculatePriceLoading(false);
      }).catch((err) => {
        //stop loading 
        setRecalculatePriceLoading(false);
        console.log(err.message);
      });

  }

  function resetBulkUpdateVariables() {
    setStatusBulkUpdate(null);
    setCurrentLocationBulkUpdate(null);
    setEventTypeBulkUpdate(null);
    setBulkUpdateDescription('');
    setBulkTrackingEventMode('record');
    setBulkTrackingEventTimestamp(defaultDatetimeLocalValue());
    setBulkTrackingEventTimestampError('');
    setPaidBulkUpdate(null);
    setSendMessage(false);
  }

  //submit form 
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (viewMode === 'deleted') {
      fetchDeletedData(true);
    } else {
      fetchData(true);
    }
  }

  useEffect(() => {
    fetchData();
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    if (viewMode === 'deleted') {
      fetchDeletedData();
    }
  }, [viewMode, deletedPaginationModel.page, deletedPaginationModel.pageSize]);

  // Bulk update shipments and send messages
  const bulkUpdateShipmentsControl = async (event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (!hasPermission(PERMISSIONS.SHIPMENTS_BULK_UPDATE)) return;

    const hasTrackingFieldChanges = !!(
      statusBulkUpdate || currentLocationBulkUpdate || eventTypeBulkUpdate
    );

    const timestampError = validateTrackingEventFormFields(
      bulkTrackingEventMode,
      bulkTrackingEventTimestamp,
      t,
    );
    if (hasTrackingFieldChanges && timestampError) {
      setBulkTrackingEventTimestampError(timestampError);
      return;
    }
    setBulkTrackingEventTimestampError('');
    const trackingDecision = hasTrackingFieldChanges
      ? trackingDecisionFromFormFields(
          bulkTrackingEventMode,
          bulkTrackingEventTimestamp,
        )
      : { mode: 'omit' };

    setBulkUpdateLoading(true);
    let data = {
      shipments_ids: selectionModel,
      ...(currentLocationBulkUpdate && { location: currentLocationBulkUpdate.id }),
      ...(statusBulkUpdate && { status: statusBulkUpdate }),
      ...(eventTypeBulkUpdate?.code && { event_type: eventTypeBulkUpdate.code }),
      ...(bulkUpdateDescription.trim() && { description: bulkUpdateDescription.trim() }),
      send_shipments_messages: sendMessage,
    };
    data = applyTrackingEventControl(data, trackingDecision);

    try {
      const response = await bulkUpdateShipments(data);
      if (response.status === 201) {
        setResponseMessage(response.data.message);
        setSuccessNotification(true);
        setOpenBulkUpdateDialog(false);
        await fetchData(true);

      } else {
        setResponseMessage(t("shipments.errorUpdatingShipments"));
        setFailedNotification(true);
      }
    } catch (err) {
      setResponseMessage(t("shipments.deleteErrorGeneric", { message: err.message }));
      setFailedNotification(true);
    } finally {
      setBulkUpdateLoading(false);
      setOpenBulkUpdateDialog(false);
      resetBulkUpdateVariables();
    }
  };

  const resetBulkTagsDialog = () => {
    setBulkTagNames([]);
    setBulkTagsAction('add');
  };

  const bulkTagsActionOptions = useMemo(
    () => [
      {
        value: 'add',
        title: t('shipments.bulkTagsAddTitle'),
        description: t('shipments.bulkTagsAddDesc'),
      },
      {
        value: 'replace',
        title: t('shipments.bulkTagsReplaceTitle'),
        description: t('shipments.bulkTagsReplaceDesc'),
      },
      {
        value: 'remove',
        title: t('shipments.bulkTagsRemoveTitle'),
        description: t('shipments.bulkTagsRemoveDesc'),
      },
    ],
    [t],
  );

  const bulkTagsSubmit = async () => {
    if (selectionModel.length === 0) return;
    if (!hasPermission(PERMISSIONS.SHIPMENTS_BULK_TAGS)) return;
    const shipmentIds = selectionModel.map((id) => Number(id));
    if ((bulkTagsAction === 'add' || bulkTagsAction === 'remove') && bulkTagNames.length === 0) {
      setResponseMessage(t("shipments.bulkTagsChooseOne"));
      setFailedNotification(true);
      return;
    }

    setBulkTagsLoading(true);
    try {
      const response = await bulkShipmentTags({
        shipment_ids: shipmentIds,
        action: bulkTagsAction,
        tags: bulkTagNames,
      });
      if (response.status === 200) {
        const updated = response.data?.updated ?? shipmentIds.length;
        setResponseMessage(t("shipments.tagsUpdatedCount", { count: updated }));
        setSuccessNotification(true);
        setOpenBulkTagsDialog(false);
        resetBulkTagsDialog();
        await fetchData(true);
      } else {
        setResponseMessage(t("shipments.tagsUpdateFailed"));
        setFailedNotification(true);
      }
    } catch (err) {
      const res = err?.response;
      const d = res?.data;
      let msg =
        d?.error ||
        d?.detail ||
        (d?.tags && (Array.isArray(d.tags) ? d.tags.join(' ') : String(d.tags))) ||
        (d?.action && (Array.isArray(d.action) ? d.action.join(' ') : String(d.action))) ||
        err?.message ||
        t("shipments.tagsUpdateFailed");
      if (res?.status === 404 && Array.isArray(d?.missing_ids) && d.missing_ids.length > 0) {
        msg = t("shipments.bulkTagsNotFoundFull", {
          error: d?.error || t("shipments.bulkTagsMissingDefault"),
          ids: d.missing_ids.join(', '),
        });
      }
      if (res?.status === 403) {
        msg = t("shipments.bulkTagsPermission");
      }
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setBulkTagsLoading(false);
    }
  };

  const assignShipmentsToUnitSubmit = async () => {
    if (selectionModel.length === 0) return;
    setAssignShipmentsToUnitLoading(true);
    try {
      const ids = selectionModel.map((id) => Number(id));
      const unitId = assignToUnitShipmentUnit?.id;
      const response = await bulkAssignShipmentsToShipmentUnit(
        unitId != null
          ? { shipments: ids, shipment_unit: unitId }
          : { shipments: ids, shipment_unit: null },
      );
      if (response.status === 200) {
        if (unitId != null) {
          const label =
            assignToUnitShipmentUnit.serial_number != null &&
            String(assignToUnitShipmentUnit.serial_number).trim()
              ? String(assignToUnitShipmentUnit.serial_number).trim()
              : `unit #${unitId}`;
          setResponseMessage(t("shipments.assignedToUnit", { count: ids.length, label }));
        } else {
          setResponseMessage(t("shipments.removedFromUnit", { count: ids.length }));
        }
        setSuccessNotification(true);
        setOpenAssignShipmentsToUnitDialog(false);
        setAssignToUnitShipmentUnit(null);
        await fetchData(true);
      } else {
        setResponseMessage(
          unitId != null
            ? t("shipments.couldNotAssignUnit")
            : t("shipments.couldNotRemoveFromUnit"),
        );
        setFailedNotification(true);
      }
    } catch (err) {
      const res = err?.response;
      const d = res?.data;
      let msg =
        (d?.shipments &&
          (Array.isArray(d.shipments) ? d.shipments.join(' ') : String(d.shipments))) ||
        d?.detail ||
        d?.error ||
        err?.message ||
        (assignToUnitShipmentUnit?.id != null
          ? t("shipments.failedAssign")
          : t("shipments.failedRemoveFromUnit"));
      if (res?.status === 403) {
        msg = t("shipments.permissionAssignUnit");
      }
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setAssignShipmentsToUnitLoading(false);
    }
  };

  const openReceiveShipmentsModal = () => {
    const shipment =
      selectedShipments[0]
      ?? results.find((row) => isRowSelectedInSelectionModel(selectionModel, row.id));
    const receiverDetails = shipment?.receiver_details;
    const owner = shipment?.owner;

    setCivilId(
      receiverDetails?.receiver_civil_id != null && String(receiverDetails.receiver_civil_id).trim() !== ''
        ? String(receiverDetails.receiver_civil_id)
        : owner?.id_number != null && String(owner.id_number).trim() !== ''
          ? String(owner.id_number)
          : ''
    );
    setReceiverName(
      receiverDetails?.receiver_name != null && String(receiverDetails.receiver_name).trim() !== ''
        ? String(receiverDetails.receiver_name)
        : getLocalizedUserFullName(owner, i18n.language)
    );
    setReceiverPhoneNumber(
      receiverDetails?.receiver_phone_number != null && String(receiverDetails.receiver_phone_number).trim() !== ''
        ? String(receiverDetails.receiver_phone_number)
        : owner?.phone_number != null && String(owner.phone_number).trim() !== ''
          ? String(owner.phone_number)
          : '+968'
    );
    setReceiveDestinationType(
      receiverDetails?.destination_location_type
        ? String(receiverDetails.destination_location_type).toLowerCase()
        : 'home'
    );
    setReceiveDestinationLocation(
      receiverDetails?.destination_location
        ? String(receiverDetails.destination_location)
        : ''
    );
    setReceiveShipmentStatus('delivered');
    const eventTypeCode =
      shipment?.event_type != null && String(shipment.event_type).trim() !== ''
        ? String(shipment.event_type).trim()
        : 'delivered';
    setReceiveShipmentEventType({
      code: eventTypeCode,
      name: shipment?.event_type_name ?? eventTypeCode,
    });
    setOpenReceiveShipmentsAlert(true);
  };

  const receiveShipments = async () => {
    if (!hasPermission(PERMISSIONS.SHIPMENTS_RECEIVE)) return;
    if (!receiveShipmentEventType?.code) {
      setResponseMessage(t('trackingEvent.errEventTypeRequired'));
      setFailedNotification(true);
      return;
    }
    setIsReceiveShipmentLoading(true);
    // Collect data
    const payload = {
      'shipments_ids': selectionModel,
      'receiver_civil_id': civilId != null ? civilId : "",
      'receiver_phone_number': receiverPhoneNumber != null ? receiverPhoneNumber : "",
      'receiver_name': receiverName != null ? receiverName : "",
      'destination_location_type': receiveDestinationType,
      'destination_location': receiveDestinationLocation,
      'status': receiveShipmentStatus,
      'event_type': receiveShipmentEventType.code,
      'send_shipments_messages': sendShipmentsMessages
    };
    console.log(payload);

    try {
      var response = await receiveShipmentsRequest(payload).then(async actualData => {
        //control success
        if (actualData.status == 200) {
          setResponseMessage(t("shipments.receiverRegistered"));
          //send notification
          setSuccessNotification(true);
          setOpenReceiveShipmentsAlert(false);
          resetFields(); // Reset variables
          await fetchData(true);
          //fetch invoices again
        }
        //control other responses
        else {
          setResponseMessage(t("shipments.errorInvoiceReceiver"));
          //send notification
          setFailedNotification(true);
        }
        //stop loading 
        setIsReceiveShipmentLoading(false);
      }).catch((err) => {

        //stop loading 
        setIsReceiveShipmentLoading(false);
        console.log(err.message);
      });

      if (response.ok) {
        const result = await response.json();

        console.log('Record saved successfully:', result);

        // Reset variables
        resetFields();
        setOpenReceiveShipmentsAlert(false);
      } else {
        console.error('Failed to save the record:', response.statusText);
      }
      setIsReceiveShipmentLoading(false);
    } catch (error) {
      setIsReceiveShipmentLoading(false);
      console.error('Error saving the record:', error);
    }
  };

  const handlePaymentShipments = async () => {
    if (!hasPermission(PERMISSIONS.PAYMENTS_ADD_SHIPMENT)) return;
    setIsPayShipmentLoading(true);
    
    // Validation: Check if all selected shipments are invoiced
    const uninvoicedShipments = selectedShipments.filter(row => !row.invoiced);
    if (uninvoicedShipments.length > 0) {
      setResponseMessage(t("shipments.paymentNotInvoiced"));
      setFailedNotification(true);
      setIsPayShipmentLoading(false);
      setOpenPayShipmentsAlert(false);
      return;
    }
    if (selectedShipmentsHaveDifferentInvoices(selectedShipments)) {
      setResponseMessage(t("shipments.paymentDifferentInvoices"));
      setFailedNotification(true);
      setIsPayShipmentLoading(false);
      setOpenPayShipmentsAlert(false);
      return;
    }
    
    // Validation: Check if required fields are filled
    if (!account) {
      setResponseMessage(t("shipments.accountRequired"));
      setFailedNotification(true);
      setIsPayShipmentLoading(false);
      return;
    }
    if (!paymentTransactionDate) {
      setResponseMessage(t("shipments.transactionDateRequired"));
      setFailedNotification(true);
      setIsPayShipmentLoading(false);
      return;
    }
    if (!isPositivePaymentAmount(totalAmount)) {
      setResponseMessage(t("shipments.paymentAmountPositive"));
      setFailedNotification(true);
      setIsPayShipmentLoading(false);
      return;
    }

    // Collect data
    const payload = {
      'shipment_ids': selectionModel,
      'payment_data': {
        'total_amount': totalAmount,
        'transaction_reference': transactionReference,
        'payment_method': paymentMethod,
        'account': account.id,
        'transaction_date': paymentTransactionDate
      }

    };

    try {
      await addShipmentPayment(payload).then(
        async actualData => {
          if (actualData.status === 200) {
            setResponseMessage(t("shipments.paymentRegistered"));
            setSuccessNotification(true);
            setOpenPayShipmentsAlert(false);
            resetFields(); // Reset variables
            await fetchData(true);
          } else {
            setResponseMessage(t("shipments.paymentProcessError"));
            setFailedNotification(true);
          }
          setIsPayShipmentLoading(false);

        });


    } catch (error) {
      setIsPayShipmentLoading(false);
      setResponseMessage(error.message);
      setFailedNotification(true);
      console.error('Error processing the payment:', error);
    }
  };


  //generate invoices function 
  const showInvoiceGenerationNotification = (result) => {
    setSuccessNotification(false);
    setFailedNotification(false);
    setWarningNotification(false);
    setResponseMessage(result.message);
    if (result.type === 'success') setSuccessNotification(true);
    else if (result.type === 'warning') setWarningNotification(true);
    else setFailedNotification(true);
  };

  const generateInvoices = async () => {
    setIsGeneratingInvoicesLoading(true);
    try {
      const actualData = await generateInvoicesForShipmentsRequest(selectionModel, sendInvoicesMessages);
      const result = getInvoiceGenerationNotification(actualData.data, t, 'create');
      showInvoiceGenerationNotification(result);
      if (result.refresh) {
        setOpenGenerateInvoicesAlert(false);
        await fetchData(true);
      }
    } catch (err) {
      const parsed = parseGenerateInvoicesForShipmentsError(err, t);
      if (parsed.isZeroPrice) {
        setZeroPriceHighlightIds(parsed.zeroPriceShipmentIds);
      }
      showInvoiceGenerationNotification({
        type: 'error',
        message: parsed.message,
      });
    } finally {
      setIsGeneratingInvoicesLoading(false);
    }
  }

  const piecesFromShipmentsForStickers = (shipments) => {
    if (!Array.isArray(shipments)) return [];
    return shipments.flatMap((shipment) => {
      const embedded = Array.isArray(shipment.pieces) ? shipment.pieces : [];
      return embedded.map((piece) => ({
        ...piece,
        shipment,
        width_cm: piece.width_cm ?? shipment.width_cm,
        length_cm: piece.length_cm ?? shipment.length_cm,
        height_cm: piece.height_cm ?? shipment.height_cm,
        weight_kg: piece.weight_kg ?? shipment.weight,
        created_at: piece.last_event_at || piece.created_at || shipment.created_at,
      }));
    });
  };

  const handlePrintPieceStickers = () => {
    if (selectionModel.length === 0) return;
    const pieces = piecesFromShipmentsForStickers(selectedShipments);
    if (!pieces.length) {
      setResponseMessage(t('shipments.noPiecesForSelectedShipments'));
      setFailedNotification(true);
      return;
    }
    const canPrint =
      pieces.length === 1 ? canPrintPieceLabel : canPrintPieceLabelBatch;
    if (!canPrint) {
      setResponseMessage(t('permissions.noPermission'));
      setFailedNotification(true);
      return;
    }
    setStickerPieces(pieces);
    setPiecesStickersWindowOpened(true);
  };

  // Function to reset form fields of receiver info
  const resetFields = () => {
    setCivilId('');
    setReceiverName('');
    setReceiverPhoneNumber('+968')
    setReceiveShipmentStatus('delivered');
    setReceiveShipmentEventType(null);
    setReceiveDestinationType('home');
    setReceiveDestinationLocation('');
    setIsPayShipmentLoading(false);
    setOpenPayShipmentsAlert(false);
    setTotalAmount("");
    setTransactionReference("");
    setPaymentMethod("");
    setAccount(null);
    setPaymentTransactionDate(getTodayDate);

  };

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width={"100%"}>
            <Box>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                {[t("shipments.breadcrumb1"), t("shipments.breadcrumb2")].map((item) => (
                  <Link key={item} color="neutral" href="/shipments">
                    {item}
                  </Link>
                ))}
              </Breadcrumbs>
              <Typography textAlign={"start"} level="h1">{t("shipments.pageTitle")}</Typography>
            </Box>

            {/* Active / Deleted view toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1, flexWrap: 'wrap' }}>
              <Typography level="body-sm" sx={{ fontWeight: 600, color: 'text.secondary', mr: 0.5 }}>{t('shipments.viewLabel')}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="sm"
                  variant={viewMode === 'active' ? 'soft' : 'outlined'}
                  color="primary"
                  onClick={() => setViewMode('active')}
                >
                  {t('shipments.activeShipmentsTab')}
                </Button>
                <PermissionGate permission={PERMISSIONS.SHIPMENTS_DELETED_LIST}>
                  <Button
                    size="sm"
                    variant={viewMode === 'deleted' ? 'soft' : 'outlined'}
                    color="neutral"
                    startDecorator={<DeleteSweepIcon />}
                    onClick={() => setViewMode('deleted')}
                  >
                    {t('shipments.deletedShipmentsTab')}
                  </Button>
                </PermissionGate>
              </Box>
              {viewMode === 'deleted' && (
                <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                  {t('shipments.deletedReadOnlyHint')}
                </Typography>
              )}
            </Box>

            {/* Recover selected (only when viewing deleted) */}
            {viewMode === 'deleted' && (
              <PermissionGate permission={PERMISSIONS.SHIPMENTS_RECOVER}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1 }}>
                  <Tooltip title={deletedSelectionModel.length === 0 ? t('shipments.recoverSelectFirst') : t('shipments.recoverCountSelected', { count: deletedSelectionModel.length })}>
                    <span>
                      <Button
                        size="sm"
                        variant="soft"
                        color="success"
                        startDecorator={<RestoreFromTrashIcon />}
                        disabled={deletedSelectionModel.length === 0}
                        loading={recoverLoading}
                        onClick={() => setOpenRecoverAlert(true)}
                      >
                        {t('shipments.recoverSelected')}
                      </Button>
                    </span>
                  </Tooltip>
                  {deletedSelectionModel.length > 0 && (
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {t('shipments.selectedCount', { count: deletedSelectionModel.length })}
                    </Typography>
                  )}
                </Box>
              </PermissionGate>
            )}

            {/* Filter shipments field */}
            <Accordion defaultExpanded={window.innerWidth > 600}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="filter-content"
                id="filter-header"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Typography level="h3">{t('shipments.filterTitle')}</Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{ overflow: 'visible' }}
                slotProps={{
                  content: {
                    sx: { overflow: 'visible' },
                  },
                }}
              >
                <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1, overflow: 'visible' }}>
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <CustomTextField type="text" onChange={(e) => setId(e.target.value)} value={id} label={t('shipments.filterIdsPlaceholder')} />
                  </Grid>
                  {canListCustomers && (
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <CustomerOptionList
                      label={t('shipments.colCustomer')}
                      defaultValue={shipmentOwner}
                      onChange={(event, value) => setShipmentOwner(value)}
                    />
                  </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <CustomTextField type="text" onChange={(e) => setShipperReference(e.target.value)} value={shipperReference} label={t('shipments.shipperReference')} />
                  </Grid>
                  {canListShipmentTypes && (
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <ShipmentTypeOptionList
                      label={t('widgets.shipmentType')}
                      showAll={true}
                      onChange={(event, value) => setType(value)}
                    />
                  </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <CustomTextField
                      slotProps={{ input: { min: 0, max: 999999999999999 } }}
                      type="text"
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      value={trackingNumber}
                      label={t('shipments.trackingNumber')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <StatusOptionList
                      label={t('shipments.colStatus')}
                      required={false}
                      status={status}
                      setStatus={(newValue) => setStatus(newValue)}
                      showAll={true}
                      disabled={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <PaymentStatusOptionList
                      label={t('shipments.paymentStatus')}
                      required={false}
                      paymentStatus={paymentStatus}
                      setPaymentStatus={(newValue) => setPaymentStatus(newValue)}
                      showAll={true}
                      disabled={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <InvoicedOptionList
                      label={t('shipments.colInvoiced')}
                      required={false}
                      invoiced={invoiced}
                      setInvoiced={(newValue) => setInvoiced(newValue)}
                      showAll={true}
                      disabled={false}
                    />
                  </Grid>
                  {canListLocations && (
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <LocationOptionList
                      label={t('shipments.colLocation')}
                      defaultValue={currentLocation}
                      onChange={(event, value) => setCurrentLocation(value)}
                    />
                  </Grid>
                  )}
                  {canListShipmentUnits && (
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <ShipmentUnitOptionList
                      required={false}
                      label={t('shipments.assignUnitLabel')}
                      defaultValue={shipmentUnitFilter}
                      onChange={(event, value) => setShipmentUnitFilter(value)}
                    />
                  </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <CustomTextField
                      type="date"
                      label={t('shipments.shipmentTxnDateFrom')}
                      value={shipmentTransactionDateGte ?? ""}
                      onChange={(e) => setShipmentTransactionDateGte(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3}>
                    <CustomTextField
                      type="date"
                      label={t('shipments.shipmentTxnDateTo')}
                      value={shipmentTransactionDateLte ?? ""}
                      onChange={(e) => setShipmentTransactionDateLte(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6}>
                    <ShipmentTagsOptionList
                      filterMode
                      value={tagFilterNames}
                      onChange={setTagFilterNames}
                      label={t('shipments.tagsHasAny')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        overflow: 'visible',
                        boxSizing: 'border-box',
                        pt: 1,
                      }}
                    >
                      <Button
                        type="submit"
                        loading={isLoading}
                        sx={{ flexShrink: 0, maxWidth: '100%' }}
                      >
                        {t('shipments.search')}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Actions icons only — hidden when viewing deleted shipments */}
            {viewMode === 'active' && (
            <Grid mt={2} container spacing={2} justifyContent="flex-end">
              <Grid
                container
                spacing={1}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start'
                }}
              >
                <PermissionGate permission={PERMISSIONS.PAYMENTS_ADD_SHIPMENT}>
                  <Grid item>
                    <Tooltip title={payShipmentsTooltip}>
                      <span>
                        <IconButton
                          onClick={() => setOpenPayShipmentsAlert(true)}
                          disabled={isPayShipmentsDisabled}
                          color="success"
                          size="large"
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: '100%', md: 'auto' }
                          }}
                        >
                          <PriceCheckIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.INVOICES_GENERATE_FOR_SHIPMENTS}>
                  <Grid item>
                    <Tooltip title={generateInvoicesTooltip}>
                      <span>
                        <IconButton
                          onClick={() => setOpenGenerateInvoicesAlert(true)}
                          loading={isGeneratingInvoicesLoading}
                          disabled={isGenerateInvoicesDisabled}
                        color="primary"
                        size="large"
                        sx={{
                          mr: { xs: 0, md: 1 },
                          width: { xs: '100%', md: 'auto' },
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.5rem'
                          }
                        }}
                      >
                        <Receipt />
                      </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </PermissionGate>

                <PermissionGate permission={PERMISSIONS.SHIPMENTS_RECEIVE}>
                  <Grid item>
                    <Tooltip title={t('shipments.tooltipMarkReceived')}>
                      <IconButton
                        onClick={openReceiveShipmentsModal}
                        disabled={selectionModel.length <= 0}
                        color="primary"
                        size="large"
                        sx={{
                          mr: { xs: 0, md: 1 },
                          width: { xs: '100%', md: 'auto' }
                        }}
                      >
                        <LaunchIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.SHIPMENTS_RECALCULATE_PRICE}>
                  <Grid item>
                    <Tooltip title={t('shipments.tooltipRecalculatePrices')}>
                      <IconButton
                        onClick={() => setOpenRecalculatePriceAlert(true)}
                        disabled={selectionModel.length === 0}
                        color="warning"
                        size="large"
                        sx={{
                          mr: { xs: 0, md: 1 },
                          width: { xs: '100%', md: 'auto' }
                        }}
                      >
                        <CurrencyExchangeIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.WHATSAPP_BULK_SEND_SHIPMENTS}>
                  <Grid item>
                    <Tooltip title={t('shipments.tooltipWhatsApp')}>
                      <IconButton
                        onClick={() => setOpenSendMessageAlert(true)}
                        disabled={selectionModel.length === 0}
                        color="success"
                        size="large"
                        sx={{
                          mr: { xs: 0, md: 1 },
                          width: { xs: '100%', md: 'auto' }
                        }}
                      >
                        <WhatsAppIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
                <Grid item>
                  <Tooltip title={t('shipments.tooltipPrintStickers')}>
                    <IconButton
                      onClick={() => SetShipmentsStickersWindowOpened(true)}
                      disabled={selectionModel.length === 0}
                      color="info"
                      size="large"
                      sx={{
                        mr: { xs: 0, md: 1 },
                        width: { xs: '100%', md: 'auto' }
                      }}
                    >
                      <SubtitlesIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
                {(canPrintPieceLabel || canPrintPieceLabelBatch) && (
                  <Grid item>
                    <Tooltip
                      title={
                        selectionModel.length === 0
                          ? t('shipments.tooltipPrintPieceStickersSelect')
                          : t('shipments.tooltipPrintPieceStickers')
                      }
                    >
                      <span>
                        <IconButton
                          onClick={handlePrintPieceStickers}
                          disabled={selectionModel.length === 0}
                          color="info"
                          size="large"
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: '100%', md: 'auto' },
                          }}
                        >
                          <SubtitlesOutlinedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                )}
                <PermissionGate permission={PERMISSIONS.SHIPMENTS_BULK_UPDATE}>
                  <Grid item>
                    <Tooltip title={t('shipments.tooltipBulkUpdate')}>
                      <IconButton
                        onClick={() => {
                          resetBulkUpdateVariables();
                          setOpenBulkUpdateDialog(true);
                        }}
                        disabled={selectionModel.length === 0}
                        color="warning"
                        size="large"
                        sx={{
                          mr: { xs: 0, md: 1 },
                          width: { xs: '100%', md: 'auto' }
                        }}
                      >
                        <ListAltIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.SHIPMENTS_BULK_TAGS}>
                  <Grid item>
                    <Tooltip
                      title={
                        selectionModel.length === 0
                          ? t('shipments.tooltipBulkTagsEmpty')
                          : t('shipments.tooltipBulkTags')
                      }
                    >
                      <span>
                        <IconButton
                          onClick={() => {
                            resetBulkTagsDialog();
                            setOpenBulkTagsDialog(true);
                          }}
                          disabled={selectionModel.length === 0}
                          color="primary"
                          size="large"
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: '100%', md: 'auto' },
                          }}
                        >
                          <LabelIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.SHIPMENT_UNITS_ASSIGN_SHIPMENTS}>
                  <Grid item>
                    <Tooltip
                      title={
                        selectionModel.length === 0
                          ? t('shipments.tooltipAssignEmpty')
                          : t('shipments.tooltipAssignUnit')
                      }
                    >
                      <span>
                        <IconButton
                          onClick={() => {
                            setAssignToUnitShipmentUnit(null);
                            setOpenAssignShipmentsToUnitDialog(true);
                          }}
                          disabled={selectionModel.length === 0}
                          color="primary"
                          size="large"
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: '100%', md: 'auto' }
                          }}
                        >
                          <Inventory2Icon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.SHIPMENTS_BULK_DELETE}>
                  <Grid item>
                    <Tooltip title={t('shipments.tooltipDelete')}>
                      <IconButton
                        onClick={() => setDeleteAlert(true)}
                        disabled={selectionModel.length === 0}
                        color="danger"
                        size="large"
                        sx={{
                          mr: { xs: 0, md: 1 },
                          width: { xs: '100%', md: 'auto' }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.SHIPMENTS_UPLOAD_EXCEL}>
                  <Grid item>
                    <Tooltip title={t('shipments.tooltipBulkImport')}>
                      <IconButton
                        onClick={() => navigate("../../Shipment/import")}
                        color="primary"
                        size="large"
                        sx={{
                          mr: { xs: 0, md: 1 },
                          width: { xs: '100%', md: 'auto' }
                        }}
                      >
                        <Add /> {t('shipments.bulkImportSuffix')}
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.SHIPMENTS_CREATE}>
                  <Grid item>
                    <Tooltip title={t('shipments.tooltipAddNew')}>
                      <IconButton
                        onClick={() => navigate("../../Shipment/AddShipment")}
                        color="primary"
                        size="large"
                        sx={{
                          width: { xs: '100%', md: 'auto' }
                        }}
                      >
                        <Add />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </PermissionGate>
              </Grid>
            </Grid>
            )}

            {/* Compact selection summary — primary metrics + grouped status chips */}
            {selectedSummary.count > 0 && (
              <Sheet
                variant="outlined"
                sx={{
                  mt: 1,
                  mb: 0.75,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: '8px',
                  bgcolor: '#f0f7ff',
                  borderColor: '#bfdbfe',
                  borderInlineStart: '3px solid',
                  borderInlineStartColor: '#2563eb',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    minWidth: 0,
                  }}
                >
                  <Chip
                    size="sm"
                    variant="solid"
                    color="primary"
                    sx={{
                      '--Chip-minHeight': '22px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      px: 0.75,
                    }}
                  >
                    {t('shipments.selectedCount', { count: selectedSummary.count })}
                  </Chip>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 0.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600 }}>
                      {t('shipments.summaryTotalPrice')}
                    </Typography>
                    <Typography
                      level="body-sm"
                      sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}
                    >
                      <OmrAmount value={selectedSummary.totalPrice} empty="—" />
                    </Typography>
                  </Box>

                  <Divider orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' }, my: 0.25 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      columnGap: 1.25,
                      rowGap: 0.25,
                      minWidth: 0,
                    }}
                  >
                    {[
                      [t('shipments.summaryWeight'), Number(selectedSummary.totalWeight.toFixed(3))],
                      [t('shipments.summaryVolWeight'), Number(selectedSummary.totalVolWeight.toFixed(3))],
                      [t('shipments.summaryPieces'), selectedSummary.piecesCount],
                      [t('shipments.summaryCustomers'), selectedSummary.uniqueCustomers],
                      [t('shipments.summaryLocations'), selectedSummary.uniqueLocations],
                    ].map(([label, value]) => (
                      <Typography
                        key={label}
                        level="body-xs"
                        sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                      >
                        <Box component="span" sx={{ color: 'text.tertiary', fontWeight: 600 }}>
                          {label}{' '}
                        </Box>
                        <Box component="span" sx={{ fontWeight: 700, color: '#334155' }}>
                          {value}
                        </Box>
                      </Typography>
                    ))}
                  </Box>

                  <Tooltip title={t('shipments.clearSelection')} placement="top">
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      aria-label={t('shipments.clearSelection')}
                      onClick={() => {
                        if (viewMode === 'deleted') setDeletedSelectionModel([]);
                        else setSelectionModel([]);
                      }}
                      sx={{
                        ml: 'auto',
                        '--IconButton-size': '26px',
                        color: 'text.tertiary',
                        '&:hover': { color: 'danger.600', bgcolor: 'danger.softBg' },
                      }}
                    >
                      <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 0.75,
                    pt: 0.75,
                    borderTop: '1px solid',
                    borderColor: 'rgba(37, 99, 235, 0.12)',
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      level="body-xs"
                      sx={{ fontWeight: 700, color: 'text.tertiary', mr: 0.25, whiteSpace: 'nowrap' }}
                    >
                      {t('shipments.summaryPaymentStatus')}
                    </Typography>
                    {selectedSummary.paymentStatusCounts.map(([status, count]) => {
                      const chipStyle = getPaymentStatusChipStyle(status === '—' ? null : status);
                      return (
                        <Chip
                          key={`pay-${status}`}
                          size="sm"
                          variant="soft"
                          sx={{
                            '--Chip-minHeight': '20px',
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            px: 0.6,
                            ...chipStyle,
                          }}
                        >
                          {status === '—' ? '—' : formatPaymentStatus(status)} · {count}
                        </Chip>
                      );
                    })}
                  </Box>

                  <Divider orientation="vertical" sx={{ display: { xs: 'none', md: 'block' }, mx: 0.25 }} />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      level="body-xs"
                      sx={{ fontWeight: 700, color: 'text.tertiary', mr: 0.25, whiteSpace: 'nowrap' }}
                    >
                      {t('shipments.summaryStatus')}
                    </Typography>
                    {selectedSummary.statusCounts.map(([status, count]) => {
                      const chipStyle = shipmentStatusChipStyle(status === '—' ? null : status);
                      return (
                        <Chip
                          key={`st-${status}`}
                          size="sm"
                          variant="soft"
                          sx={{
                            '--Chip-minHeight': '20px',
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            px: 0.6,
                            ...chipStyle,
                          }}
                        >
                          {status === '—' ? '—' : formatShipmentStatusLabel(status, t)} · {count}
                        </Chip>
                      );
                    })}
                  </Box>

                  <Divider orientation="vertical" sx={{ display: { xs: 'none', md: 'block' }, mx: 0.25 }} />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      level="body-xs"
                      sx={{ fontWeight: 700, color: 'text.tertiary', mr: 0.25, whiteSpace: 'nowrap' }}
                    >
                      {t('shipments.summaryInvoicing')}
                    </Typography>
                    {selectedSummary.invoicedCount > 0 && (
                      <Chip
                        size="sm"
                        variant="soft"
                        color="success"
                        sx={{ '--Chip-minHeight': '20px', fontSize: '0.68rem', fontWeight: 600, px: 0.6 }}
                      >
                        {t('shipments.summaryInvoicedCount', { count: selectedSummary.invoicedCount })}
                      </Chip>
                    )}
                    {selectedSummary.notInvoicedCount > 0 && (
                      <Chip
                        size="sm"
                        variant="soft"
                        color="danger"
                        sx={{ '--Chip-minHeight': '20px', fontSize: '0.68rem', fontWeight: 600, px: 0.6 }}
                      >
                        {t('shipments.summaryNotInvoicedCount', { count: selectedSummary.notInvoicedCount })}
                      </Chip>
                    )}
                    {selectedSummary.zeroPriceCount > 0 && (
                      <Chip
                        size="sm"
                        variant="soft"
                        color="warning"
                        sx={{ '--Chip-minHeight': '20px', fontSize: '0.68rem', fontWeight: 600, px: 0.6 }}
                      >
                        {t('shipments.summaryZeroPriceCount', { count: selectedSummary.zeroPriceCount })}
                      </Chip>
                    )}
                  </Box>

                  {selectedSummary.locationCounts.length > 0 && (
                    <>
                      <Divider orientation="vertical" sx={{ display: { xs: 'none', lg: 'block' }, mx: 0.25 }} />
                      <Box
                        sx={{
                          display: { xs: 'none', lg: 'flex' },
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          level="body-xs"
                          sx={{ fontWeight: 700, color: 'text.tertiary', mr: 0.25, whiteSpace: 'nowrap' }}
                        >
                          {t('shipments.summaryByLocation')}
                        </Typography>
                        {selectedSummary.locationCounts.slice(0, 3).map(([location, count]) => (
                          <Chip
                            key={`loc-${location}`}
                            size="sm"
                            variant="outlined"
                            sx={{
                              '--Chip-minHeight': '20px',
                              fontSize: '0.68rem',
                              fontWeight: 600,
                              px: 0.6,
                              bgcolor: 'background.surface',
                            }}
                          >
                            {location} · {count}
                          </Chip>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </Sheet>
            )}

            <Box>
              {viewMode === 'active' ? (
              <DataGrid
                slots={dataGridNativeCheckboxSlots}
                checkboxSelection
                onRowSelectionModelChange={(newSelection) => {
                  setSelectionModel(newSelection);
                }}
                rowSelectionModel={selectionModel}
                onPaginationModelChange={(params) => {
                  if (params.pageSize !== paginationModel.pageSize) {
                    handlePageSizeChange(params.pageSize);
                  } else {
                    handlePageChange(params.page);
                  }
                }}
                pagination
                paginationModel={paginationModel}
                paginationMode="server"
                rowCount={data ? data.count : 0}
                rows={results || []}
                columns={columns}
                loading={isLoading}
                initialState={{ pagination: { paginationModel } }}
                pageSizeOptions={[25, 50, 100]}
                hideFooterSelectedRowCount
                getRowHeight={() => 'auto'}
                getRowClassName={(params) => (
                  zeroPriceHighlightSet.has(Number(params.id)) ? 'zero-price-shipment-row' : ''
                )}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#333',
                    },
                  },
                  '& .MuiDataGrid-cell': {
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    lineHeight: '1.5',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#555',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    borderBottom: '1px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#e3f2fd',
                      '&:hover': {
                        backgroundColor: '#bbdefb',
                      },
                    },
                  },
                  '& .zero-price-shipment-row': {
                    backgroundColor: '#fff3e0',
                    '&:hover': {
                      backgroundColor: '#ffe0b2',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#ffe0b2',
                      '&:hover': {
                        backgroundColor: '#ffcc80',
                      },
                    },
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                    fontSize: '14px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  },
                  '& .MuiDataGrid-toolbarContainer': {
                    padding: '12px',
                    backgroundColor: '#fafafa',
                    borderBottom: '1px solid #e0e0e0',
                  },
                  '@media (max-width: 600px)': {
                    '& .MuiDataGrid-toolbarContainer': {
                      flexDirection: 'column',
                      gap: 1,
                    },
                    '& .MuiDataGrid-footer': {
                      flexDirection: 'column',
                      gap: 1,
                    },
                    '& .MuiDataGrid-cell': {
                      minHeight: '60px',
                      alignItems: 'center',
                      padding: '8px',
                      fontSize: '13px',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      fontSize: '13px',
                    },
                  }
                }}
              />
              ) : (
              <DataGrid
                slots={dataGridNativeCheckboxSlots}
                checkboxSelection
                onRowSelectionModelChange={(newSelection) => setDeletedSelectionModel(newSelection)}
                rowSelectionModel={deletedSelectionModel}
                onPaginationModelChange={(params) => {
                  if (params.pageSize !== deletedPaginationModel.pageSize) {
                    setDeletedPaginationModel((prev) => ({ ...prev, page: 0, pageSize: params.pageSize }));
                  } else {
                    setDeletedPaginationModel((prev) => ({ ...prev, page: params.page }));
                  }
                }}
                pagination
                paginationModel={deletedPaginationModel}
                paginationMode="server"
                rowCount={deletedData?.count ?? 0}
                rows={deletedResults || []}
                columns={columnsDeleted}
                loading={deletedLoading}
                initialState={{ pagination: { paginationModel: deletedPaginationModel } }}
                pageSizeOptions={[25, 50, 100]}
                hideFooterSelectedRowCount={false}
                getRowHeight={() => 'auto'}
                getRowClassName={() => 'deleted-shipment-row'}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  '& .deleted-shipment-row': {
                    backgroundColor: 'rgba(0,0,0,0.02)',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  },
                  '& .MuiDataGrid-root': { border: 'none' },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333',
                                   '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '14px', color: '#333' },
                  },
                  '& .MuiDataGrid-cell': {
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    lineHeight: '1.5',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#555',
                    borderBottom: '1px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', cursor: 'pointer' },
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                    fontSize: '14px',
                  },
                }}
              />
              )}
            </Box>

            <DeleteAlert
              label={t("shipments.deleteAlertShipments")}
              open={deleteAlert}
              onDelete={() => deleteShipmentsFromTable(selectionModel)}
              onClose={() => setDeleteAlert(false)}
              loading={deleteLoading}
            />
            <DeleteAlert
              label={t("shipments.deleteAlertShipment")}
              open={deleteAlertSingal}
              onDelete={() => deleteShipmentsFromTable(selectedShipment)}
              onClose={() => setDeleteAlertSingal(false)}
              loading={deleteLoading}
            />

            {/* Recover deleted shipments confirmation */}
            <Modal open={openRecoverAlert} onClose={() => !recoverLoading && setOpenRecoverAlert(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <RestoreFromTrashIcon />
                  {t('shipments.recoverModalTitle')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {t('shipments.recoverModalBody', { count: deletedSelectionModel.length })}
                </DialogContent>
                <DialogActions>
                  <Button
                    loading={recoverLoading}
                    variant="solid"
                    color="success"
                    startDecorator={<RestoreFromTrashIcon />}
                    onClick={handleRecoverShipments}
                  >
                    {t('shipments.recoverButton')}
                  </Button>
                  <Button
                    variant="plain"
                    color="neutral"
                    disabled={recoverLoading}
                    onClick={() => setOpenRecoverAlert(false)}
                  >
                    {t('actions.cancel')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Shipments stickers window*/}
            <ShipmentsStickersWindow shipments={(results ?? []).filter(shipment => selectionModel.filter(id => id == shipment.id).length != 0)} open={shipmentsStickersWindowOpened}
              onClose={() => SetShipmentsStickersWindowOpened(false)}>

            </ShipmentsStickersWindow>

            <ShipmentPiecesStickersWindow
              pieces={stickerPieces}
              embeddedOnly
              open={piecesStickersWindowOpened}
              onClose={() => {
                setPiecesStickersWindowOpened(false);
                setStickerPieces([]);
              }}
            />

            <ShipmentTrackingModal
              open={trackingModalOpen}
              shipmentId={trackingModalShipmentId}
              onClose={() => {
                setTrackingModalOpen(false);
                setTrackingModalShipmentId(null);
              }}
            />


            {/* Regenerate Invoices Alert */}
            <Modal open={openSendMessageAlert} onClose={() => setOpenSendMessageAlert(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <WarningRoundedIcon />
                  {t('shipments.confirmation')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {t('shipments.whatsappConfirmBody')}
                </DialogContent>
                <DialogActions>
                  <Button loading={bulkSendShipmentsWhatsappMessageLoading} variant="solid" color="success" onClick={() => {
                    bulkSendShipmentsWhatsappMessageControl();
                  }}>
                    {t('actions.yes')}
                  </Button>
                  <Button variant="plain" color="neutral" onClick={() => setOpenSendMessageAlert(false)}>
                    {t('actions.no')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>


            {/* Recalculate Price Alert */}
            <Modal open={openRecalculatePriceAlert} onClose={() => setOpenRecalculatePriceAlert(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <WarningRoundedIcon />
                  {t('shipments.confirmation')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {t('shipments.recalculateConfirmBody')}
                </DialogContent>
                <DialogActions>
                  <Button loading={recalculatePriceLoading} variant="solid" color="success" onClick={() => {
                    recalculatePriceControl();
                  }}>
                    {t('actions.yes')}
                  </Button>
                  <Button variant="plain" color="neutral" onClick={() => setOpenRecalculatePriceAlert(false)}>
                    {t('actions.no')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Assign selected shipments to a shipment unit */}
            <Modal
              open={openAssignShipmentsToUnitDialog}
              onClose={() => {
                if (!assignShipmentsToUnitLoading) {
                  setOpenAssignShipmentsToUnitDialog(false);
                  setAssignToUnitShipmentUnit(null);
                }
              }}
            >
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <Inventory2Icon />
                  {t('shipments.assignUnitTitle')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {selectionModel.length > 0 ? (
                    <Typography level="body-sm" sx={{ mb: 2 }}>
                      {t('shipments.assignUnitBody', { count: selectionModel.length })}
                    </Typography>
                  ) : null}
                  {canListShipmentUnits ? (
                    <ShipmentUnitOptionList
                      required={false}
                      label={t('shipments.assignUnitLabel')}
                      defaultValue={assignToUnitShipmentUnit}
                      onChange={(e, value) => setAssignToUnitShipmentUnit(value)}
                      disabled={assignShipmentsToUnitLoading}
                    />
                  ) : (
                    <Typography level="body-sm" color="warning" sx={{ py: 1 }}>
                      {t('shipmentUnit.noListPermission')}
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    loading={assignShipmentsToUnitLoading}
                    variant="solid"
                    color={assignToUnitShipmentUnit?.id != null ? 'primary' : 'warning'}
                    onClick={assignShipmentsToUnitSubmit}
                    disabled={selectionModel.length === 0}
                  >
                    {assignToUnitShipmentUnit?.id != null
                      ? t('shipments.assignShipmentsButton')
                      : t('shipments.removeFromUnitButton')}
                  </Button>
                  <Button
                    variant="plain"
                    color="neutral"
                    disabled={assignShipmentsToUnitLoading}
                    onClick={() => {
                      setOpenAssignShipmentsToUnitDialog(false);
                      setAssignToUnitShipmentUnit(null);
                    }}
                  >
                    {t('actions.cancel')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Bulk Update Shipments */}
            <Modal
              open={openBulkUpdateDialog}
              onClose={() => {
                if (!bulkUpdateLoading) {
                  setOpenBulkUpdateDialog(false);
                  resetBulkUpdateVariables();
                }
              }}
            >
              <ModalDialog
                variant="outlined"
                role="dialog"
                aria-labelledby="bulk-update-title"
                sx={{
                  maxWidth: 560,
                  width: '95vw',
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 0,
                }}
              >
                <DialogTitle id="bulk-update-title" sx={{ px: 2.5, py: 2 }}>
                  <ListAltIcon />
                  {t('shipments.bulkUpdateTitle')}
                </DialogTitle>
                <Divider />
                <form
                  onSubmit={bulkUpdateShipmentsControl}
                  style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                >
                  <DialogContent
                    sx={{
                      px: 2.5,
                      py: 2,
                      overflow: 'auto',
                      flex: 1,
                    }}
                  >
                    <Stack spacing={2}>
                      {canListLocations && (
                        <LocationOptionList
                          label={t('shipments.colLocation')}
                          defaultValue={currentLocationBulkUpdate}
                          onChange={(event, value) => setCurrentLocationBulkUpdate(value)}
                        />
                      )}
                      <StatusOptionList
                        label={t('shipments.colStatus')}
                        required={false}
                        status={statusBulkUpdate}
                        setStatus={(newValue) => setStatusBulkUpdate(newValue)}
                        showAll={false}
                        disabled={false}
                      />
                      <TrackingEventTypeOptionList
                        key={eventTypeBulkUpdate ? `bulk-event-type-${eventTypeBulkUpdate.code}` : 'bulk-event-type-cleared'}
                        label={t('widgets.eventType')}
                        required={false}
                        defaultValue={eventTypeBulkUpdate}
                        onChange={(_, value) => setEventTypeBulkUpdate(value ?? null)}
                      />
                      <FormControl sx={{ width: '100%' }}>
                        <FormLabel>{t('shipments.bulkUpdateDescription')}</FormLabel>
                        <Textarea
                          value={bulkUpdateDescription}
                          onChange={(e) => setBulkUpdateDescription(e.target.value.slice(0, 500))}
                          minRows={2}
                          size="md"
                          placeholder={t('shipments.bulkUpdateDescriptionPlaceholder')}
                        />
                        <FormHelperText>
                          {t('shipments.bulkUpdateDescriptionHint')} ({bulkUpdateDescription.length}/500)
                        </FormHelperText>
                      </FormControl>
                      <TrackingEventControlFields
                        mode={bulkTrackingEventMode}
                        onModeChange={(value) => {
                          setBulkTrackingEventMode(value || 'auto');
                          setBulkTrackingEventTimestampError('');
                        }}
                        timestampLocal={bulkTrackingEventTimestamp}
                        onTimestampChange={(value) => {
                          setBulkTrackingEventTimestamp(value);
                          setBulkTrackingEventTimestampError('');
                        }}
                        timestampError={bulkTrackingEventTimestampError}
                      />
                      <NativeCheckbox
                        checked={sendMessage}
                        onChange={(event) => setSendMessage(event.target.checked)}
                        label={t('shipments.sendMessageCheckbox')}
                      />
                    </Stack>
                  </DialogContent>
                  <Divider />
                  <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                    <Button loading={bulkUpdateLoading} type="submit" variant="solid" color="success">
                      {t('actions.submit')}
                    </Button>
                    <Button
                      variant="plain"
                      color="neutral"
                      disabled={bulkUpdateLoading}
                      onClick={() => {
                        setOpenBulkUpdateDialog(false);
                        resetBulkUpdateVariables();
                      }}
                    >
                      {t('actions.cancel')}
                    </Button>
                  </DialogActions>
                </form>
              </ModalDialog>
            </Modal>

            {/* Bulk tags — POST /api/shipment/bulk-tags/ */}
            <Modal
              open={openBulkTagsDialog}
              onClose={() => {
                if (!bulkTagsLoading) {
                  setOpenBulkTagsDialog(false);
                  resetBulkTagsDialog();
                }
              }}
            >
              <ModalDialog
                variant="outlined"
                role="dialog"
                aria-labelledby="bulk-tags-title"
                sx={{ maxWidth: 560, width: '100%' }}
              >
                <DialogTitle id="bulk-tags-title">
                  <LabelIcon />
                  {t('shipments.bulkTagsTitle')}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ overflow: 'auto' }}>
                  <Typography level="body-sm" sx={{ mb: 2 }}>
                    {t('shipments.bulkTagsIntro', { count: selectionModel.length })}
                  </Typography>

                  <Typography level="body-xs" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                    {t('shipments.bulkTagsAction')}
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {bulkTagsActionOptions.map((opt) => (
                      <Sheet
                        key={opt.value}
                        variant={bulkTagsAction === opt.value ? 'solid' : 'outlined'}
                        color={bulkTagsAction === opt.value ? 'primary' : 'neutral'}
                        role="button"
                        tabIndex={0}
                        onClick={() => setBulkTagsAction(opt.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setBulkTagsAction(opt.value);
                          }
                        }}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderRadius: 'md',
                          transition: 'box-shadow 0.15s ease',
                          '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.500',
                            outlineOffset: 2,
                          },
                        }}
                      >
                        <Typography level="title-sm" sx={{ mb: 0.25 }}>
                          {opt.title}
                        </Typography>
                        <Typography level="body-xs" sx={{ opacity: bulkTagsAction === opt.value ? 0.95 : 0.8 }}>
                          {opt.description}
                        </Typography>
                      </Sheet>
                    ))}
                  </Stack>

                  <ShipmentTagsOptionList
                    value={bulkTagNames}
                    onChange={setBulkTagNames}
                    label={t('shipments.tagsLabel')}
                    placeholder={
                      bulkTagsAction === 'remove'
                        ? t('shipments.bulkTagsPlaceholderRemove')
                        : t('shipments.bulkTagsPlaceholderDefault')
                    }
                    disabled={bulkTagsLoading}
                    showShipmentSaveHint={false}
                  />

                  {bulkTagsAction === 'replace' && bulkTagNames.length === 0 && (
                    <Alert color="warning" variant="soft" sx={{ mt: 1 }}>
                      {t('shipments.bulkTagsReplaceWarning')}
                    </Alert>
                  )}
                  {(bulkTagsAction === 'add' || bulkTagsAction === 'remove') && bulkTagNames.length === 0 && (
                    <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary' }}>
                      {t('shipments.bulkTagsNeedOneTag', {
                        action: bulkTagsAction === 'add' ? t('shipments.bulkTagsAddAction') : t('shipments.bulkTagsRemoveAction'),
                      })}
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    loading={bulkTagsLoading}
                    variant="solid"
                    color={
                      bulkTagsAction === 'replace' && bulkTagNames.length === 0 ? 'warning' : 'primary'
                    }
                    onClick={bulkTagsSubmit}
                    disabled={
                      selectionModel.length === 0 ||
                      ((bulkTagsAction === 'add' || bulkTagsAction === 'remove') &&
                        bulkTagNames.length === 0)
                    }
                  >
                    {t('shipments.bulkTagsApply', { count: selectionModel.length })}
                  </Button>
                  <Button
                    variant="plain"
                    color="neutral"
                    disabled={bulkTagsLoading}
                    onClick={() => {
                      setOpenBulkTagsDialog(false);
                      resetBulkTagsDialog();
                    }}
                  >
                    {t('actions.cancel')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>


            {/* Receive Shipments Alert */}
            <Modal open={openReceiveShipmentsAlert} onClose={() => setOpenReceiveShipmentsAlert(false)}>
              <ModalDialog
                variant="outlined"
                sx={{
                  maxWidth: 520,
                  width: '100%',
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 0,
                  overflow: 'hidden',
                }}
              >
                <ModalClose disabled={isReceiveShipmentLoading} />
                <DialogTitle sx={{ flexShrink: 0, px: 2.5, pt: 2.5, pb: 1, alignItems: 'flex-start' }}>
                  <LaunchIcon />
                  {t('shipments.receiveModalTitle')}
                </DialogTitle>
                <Divider sx={{ flexShrink: 0 }} />
                <DialogContent
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: 2.5,
                    py: 2,
                  }}
                >
                  <Grid container spacing={1.5}>
                    <CustomTextField required type="number" onChange={(e) => setCivilId(e.target.value)} value={civilId} label={t('shipments.civilId')} xs={12} />
                    <CustomTextField required onChange={(event) => setReceiverName(event.target.value)} value={receiverName} label={t('shipments.receiverName')} xs={12} />

                    <Grid xs={12}>
                      <MuiPhone
                        key={`receive-phone-${selectionModel[0] ?? 'none'}`}
                        value={receiverPhoneNumber ?? '+968'}
                        onChange={(value) => setReceiverPhoneNumber(value)}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <CustomerDeliveryDestinationForm
                        destinationType={receiveDestinationType}
                        onDestinationTypeChange={setReceiveDestinationType}
                        destinationLocation={receiveDestinationLocation}
                        onDestinationLocationChange={setReceiveDestinationLocation}
                        siteName={siteName}
                        disabled={isReceiveShipmentLoading}
                        showComparisonHint={false}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <StatusOptionList
                        label={t('shipments.shipmentsStatus')}
                        required={true}
                        status={receiveShipmentStatus}
                        setStatus={(newValue) => setReceiveShipmentStatus(newValue)}
                        showAll={false}
                        disabled={false}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <TrackingEventTypeOptionList
                        key={
                          receiveShipmentEventType
                            ? `receive-event-type-${receiveShipmentEventType.code}`
                            : 'receive-event-type-cleared'
                        }
                        label={t('widgets.eventType')}
                        required={true}
                        defaultValue={receiveShipmentEventType}
                        onChange={(_, value) => setReceiveShipmentEventType(value ?? null)}
                        disabled={isReceiveShipmentLoading}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <NativeCheckbox
                        defaultChecked={sendShipmentsMessages}
                        checked={sendShipmentsMessages}
                        onChange={(event) => setSendShipmentsMessages(event.target.checked)}
                        label={t('shipments.sendShipmentsMsgCustomer')}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <Divider sx={{ flexShrink: 0 }} />
                <DialogActions sx={{ flexShrink: 0, px: 2.5, py: 1.5 }}>
                  <Button loading={isReceiveShipmentLoading} variant="solid" color="success" onClick={() => {
                    receiveShipments();
                  }}>
                    {t('actions.submit')}
                  </Button>
                  <Button variant="plain" color="neutral" onClick={() => {
                    setOpenReceiveShipmentsAlert(false);
                    resetFields();
                  }}>
                    {t('actions.cancel')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>


            {/* Pay Shipments Alert */}
            <Modal open={openPayShipmentsAlert} onClose={() => setOpenPayShipmentsAlert(false)}>
              {/* Keep the dialog inside the viewport; the body below scrolls instead of spilling off-screen. */}
              <ModalDialog
                variant="outlined"
                role="alertdialog"
                sx={{
                  maxWidth: 520, // dialog width cap
                  width: '100%', // use the available width up to that cap
                  maxHeight: '90vh', // never taller than the viewport
                  display: 'flex', // stack title, body, and actions
                  flexDirection: 'column',
                  p: 0, // padding lives on the sections so the scroll area is exact
                  overflow: 'hidden', // clip the dialog; only the body scrolls
                }}
              >
                {/* Title stays visible while the fields scroll. */}
                <DialogTitle sx={{ flexShrink: 0, px: 2.5, pt: 2.5, pb: 1 }}>
                  <PriceCheckIcon />
                  {t('shipments.payModalTitle')}
                </DialogTitle>
                <Divider sx={{ flexShrink: 0 }} />
                {/* Size to the fields, then shrink so the body can scroll inside the dialog. */}
                <form
                  onSubmit={(event) => {
                    event.preventDefault(); // stay on this page after submit
                    handlePaymentShipments();
                  }}
                  style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}
                >
                  {/* Scroll the fields when they are taller than the dialog. */}
                  <DialogContent
                    sx={{
                      flex: '1 1 auto', // grow with the fields, shrink when the viewport is short
                      minHeight: 0, // allow this region to shrink below its content height
                      overflowY: 'auto', // vertical scroll for the form
                      px: 2.5,
                      py: 2,
                    }}
                  >
                    <Grid container spacing={1} sx={{ flexGrow: 1 }}>
                      <CustomTextField required slotProps={{
                        input: {
                          min: 0.001,
                          max: 999999999999999,
                          step: 0.001,
                        },
                      }} type="number" onChange={(event) => setTotalAmount(event.target.value)} value={totalAmount} label={t('shipments.amount')} endDecorator={<OmrSymbol height={16} />} xs={12} ></CustomTextField>
                      <CustomTextField required onChange={(event) => setTransactionReference(event.target.value)} value={transactionReference} label={t('shipments.transactionReference')} xs={12} />
                      <Grid xs={12}>
                        <PaymentMethodOptionList
                          label={t('widgets.paymentMethod')}
                          required={true}
                          currentPaymentMethod={paymentMethod}
                          setCurrentPaymentMethod={(newValue) => setPaymentMethod(newValue)}
                          showAll={false} // Show "All" as an option
                          disabled={false} // You can disable the select if needed
                        />
                      </Grid>
                      <Grid xs={12}>
                        {canListAccounts ? (
                          <AccountOptionList
                            label={t('widgets.account')}
                            required={true}
                            currentAccount={account}
                            setCurrentAccount={(newValue) => setAccount(newValue)}
                            showActiveOnly={true}
                            disabled={false}
                          />
                        ) : (
                          <Typography level="body-sm" color="warning" sx={{ mb: 2 }}>
                            {t('backendErrors.drPermissionDenied')}
                          </Typography>
                        )}
                      </Grid>
                      <Grid xs={12}>
                        <Box sx={{ marginBottom: 2 }}>
                          <Typography
                            level="body-sm"
                            sx={{
                              fontWeight: 600,
                              color: '#555',
                              marginBottom: '6px',
                              fontSize: '14px',
                            }}
                          >
                            {t('shipments.transactionDate')} *
                          </Typography>
                          <Input
                            type="date"
                            value={paymentTransactionDate}
                            onChange={(e) => setPaymentTransactionDate(e.target.value)}
                            required
                            sx={{
                              backgroundColor: '#f7f9fc',
                              borderRadius: '8px',
                              padding: '8px',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                borderColor: '#5f09c2',
                              },
                              '&:focus': {
                                borderColor: '#5f09c2',
                                outline: 'none',
                                boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)',
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </DialogContent>
                  {/* Actions stay pinned under the scroll area. */}
                  <Divider sx={{ flexShrink: 0 }} />
                  <DialogActions sx={{ flexShrink: 0, px: 2.5, py: 1.5 }}>
                    <Button loading={isPayShipmentLoading} type="submit" variant="solid" color="success">
                      {t('actions.submit')}
                    </Button>
                    <Button variant="plain" color="neutral" onClick={() => {
                      setOpenPayShipmentsAlert(false);
                      resetFields();
                    }}>
                      {t('actions.cancel')}
                    </Button>
                  </DialogActions>
                </form>
              </ModalDialog>
            </Modal>

            {/* Generate Invoices Alert */}
            <Modal open={openGenerateInvoicesAlert} onClose={() => setOpenGenerateInvoicesAlert(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <WarningRoundedIcon />
                  {t('shipments.confirmation')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {t('shipments.generateInvoicesBody')}
                </DialogContent>
                <DialogContent>
                  <NativeCheckbox defaultChecked={sendInvoicesMessages} checked={sendInvoicesMessages} onChange={(event) => setSendInvoicesMessages(event.target.checked)} label={t('shipments.sendInvoicesMsgCustomers')} />
                </DialogContent>
                <DialogActions>
                  <Button loading={isGeneratingInvoicesLoading} variant="solid" color="success" onClick={() => {
                    generateInvoices();
                  }}>
                    {t('actions.yes')}
                  </Button>
                  <Button variant="plain" color="neutral" onClick={() => setOpenGenerateInvoicesAlert(false)}>
                    {t('actions.no')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>




            <Snackbar
              autoHideDuration={3000}
              open={successDelete}
              variant={"soft"}
              color={"success"}
              onClose={(event, reason) => {
                if (reason === 'clickaway') return;
                setSuccessDelete(false);
              }}
            >
              {t('shipments.snackbarDeletedSuccess')}
            </Snackbar>

            <Snackbar
              autoHideDuration={3000}
              open={failedDelete}
              variant={"soft"}
              color={"danger"}
              onClose={(event, reason) => {
                if (reason === 'clickaway') return;
                setFailedDelete(false);
              }}
            >
              {deleteErrorMsg}
            </Snackbar>

            {/* Generate Invoice Notification */}
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant={"soft"}
              color={"success"}
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                setSuccessNotification(false);
              }}
            >
              <Box sx={{ whiteSpace: 'pre-line' }}>{responseMessage}</Box>
            </Snackbar>
            <Snackbar
              autoHideDuration={8000}
              open={warningNotification}
              variant={"soft"}
              color={"warning"}
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                setWarningNotification(false);
              }}
            >
              <Box sx={{ whiteSpace: 'pre-line' }}>{responseMessage}</Box>
            </Snackbar>
            <Snackbar
              autoHideDuration={6000}
              open={failedNotification}
              variant={"soft"}
              color={"danger"}
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                setFailedNotification(false);
              }}
            >
              <Box sx={{ whiteSpace: 'pre-line' }}>{responseMessage}</Box>
            </Snackbar>
          </Box>
        </Box>
      </form>

    </DirectionalMaterialThemeProvider>

  );
}
